import React, { useState, useEffect, useRef } from 'react';
import { loadLeaflet, addTileLayer } from './LeafletLoader';
import GetUserCoordinates from '../../../../Cureus/Utils/GetUserCoordinates';
import BottomBackButton from './BottomBackButton';
import FullScreenButton from './FullScreenButton';
import { 
  addChannelMarkers,
  openClosestMarkerToUser
} from './Channels/AddMarkers';


const MapView = ({
  channels = [],
  selectedChannel = null,
  handleChooseChannel,
  showDefaultClosestChannel = false,
  allowSelectingChannel = false,
  showFullScreenButton = false,
  height = null,
  userId = null,
  // looks center-ish of the world
  startingCoordinates = [28.613459, -26.894531]
}) => {
  // Set height prop, or height will default to 'auto' which should be height of the container
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const selectedChannelRef = useRef(selectedChannel)
  const selectedMarkerRef = useRef(null)
  
  const [isFullScreen, setIsFullScreen] = useState(false);  
  const [userCoordinates, setUserCoordinates] = useState(null);

  const [baseZoomMin, setBaseZoomMin] = useState(null);

  const markerOptions = {
    selectedChannelRef,
    selectedMarkerRef,
    allowSelectingChannel,
    userId,
    mapInstanceRef,
    handleChooseChannel
  }

  useEffect( () => {
    // accessing state inside of event handler closures doesn't give you the most recent state, you need to use a ref
    selectedChannelRef.current = selectedChannel
  }, [selectedChannel])
  
  useEffect(() => {
    if (mapInstanceRef.current && showFullScreenButton) {
      mapInstanceRef.current.invalidateSize(false);
      if (userCoordinates === null) {
        setMinZoom(mapInstanceRef.current);
      }
      if (!isFullScreen && baseZoomMin) {
        mapInstanceRef.current.setMinZoom(baseZoomMin);
      }
    }
  }, [isFullScreen]);
  
  useEffect(() => {
    loadLeaflet(initMap);
    return () => {
      removeMapDomOnUnmount();
    };
  }, [channels]); 

  const removeMapDomOnUnmount = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  };

  const initMap = () => {
    if (!window.L) {
      console.error('Leaflet is not loaded');
      return;
    }

    const map = window.L.map(mapRef.current);
    mapInstanceRef.current = map; 

    addTileLayer(mapInstanceRef.current);
    setDefaultView(mapInstanceRef.current);
    addChannelMarkers(channels, markerOptions);
  };


  const setDefaultView = (map) => {    
    defaultWideView(map);
    
    if (selectedChannel && selectedChannel.coordinates) {
      map.setView(selectedChannel.coordinates, 12);
    } else if (showDefaultClosestChannel) {
      GetUserCoordinates()
        .then(coordinates => {
          setUserCoordinates(coordinates);
          openClosestMarkerToUser(mapInstanceRef.current, channels, coordinates, markerOptions);
        })
        .catch(error => {
          console.error('Failed to get user coordinates:', error);
        });
    }
  };

  const defaultWideView = (map) => {
    const zoomToFill = setMinZoom(map);
    setBaseZoomMin(zoomToFill);
    map.setView(startingCoordinates, zoomToFill);
  }

  const setMinZoom = (map) => {
    let bounds = window.L.latLngBounds([[-90, -180], [90, 180]]);
    const zoomToFill = map.getBoundsZoom(bounds, true);
    map.setMinZoom(zoomToFill);
    map.setMaxBounds(bounds);
    return zoomToFill;
  }

  const mapStyling = () => {
    if (isFullScreen) {
      return {
        height: 'calc(100vh - 70px)',
        width: '100vw',
        left: 0,
        bottom: 0,
        position: 'fixed'
      }
    } else {
      return {
        height: height || '100%',
        width: '100%',
        position: 'relative'
      }
    }
  }

  return (
    <div className={`map-container ${isFullScreen ? 'fullscreen' : ''}`}>
      <div className="map-content" style={mapStyling()} ref={mapRef}/>
      <FullScreenButton 
        isFullScreen={isFullScreen} 
        toggleFullScreen={setIsFullScreen} 
        showFullScreenButton={showFullScreenButton}
      />
      <BottomBackButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen}/>
    </div>
  );
};

export default MapView; 